import React, { useState } from 'react';
import { Store, ShoppingBag, Code2, Wallet } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import { Eyebrow, Tile, IconSquare } from '@/components/bento';
import MarketplaceBrowser from '@/components/marketplace/MarketplaceBrowser';
import SellSiteDialog from '@/components/marketplace/SellSiteDialog';
import AuthModal from '@/components/AuthModal';
import { useAuth } from '@/context/AuthContext';
import { MARKETPLACE, percent } from '@/config/earnings';

// ─── /Marketplace ─────────────────────────────────────────────────────────────
// Public: anyone can browse and preview. Buying and selling need an account,
// and the page asks for one at that moment rather than up front.

export default function Marketplace() {
  const { user } = useAuth();
  const [selling, setSelling] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const sell = () => (user ? setSelling(true) : setAuthOpen(true));
  const keep = percent(1 - MARKETPLACE.commissionRate);

  const STEPS = [
    { icon: ShoppingBag, title: 'Buy a finished site', body: 'Preview every page live, then pay once. It lands in your account, ready to edit.' },
    { icon: Code2, title: 'Make it yours', body: 'Change anything with a prompt or a click, publish it on your domain, or download the code.' },
    { icon: Wallet, title: `Sell yours, keep ${keep}`, body: `List a site you built. You keep ${keep} of every sale, paid out to your bank account.` },
  ];

  return (
    <div className="bg-zinc-950 min-h-screen">
      <SEOHead
        title="Website Marketplace"
        description="Buy ready-made websites built on BuildersLab, or sell the ones you build. Preview every page live, own the code, publish in minutes."
        ogUrl="https://builderslab.net/Marketplace"
      />
      <section className="pt-24 sm:pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Tile className="px-5 py-10 sm:px-10 sm:py-14 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <Eyebrow>Marketplace</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-[-0.045em] leading-[1.04] text-white">
                Websites, ready to launch
              </h1>
              <p className="mt-4 text-zinc-400 text-base sm:text-lg leading-relaxed">
                Sites built on BuildersLab by its community. Buy one and make it yours in minutes, or sell what you build.
              </p>
            </div>
            <button
              onClick={sell}
              className="shrink-0 inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-orange-500 hover:bg-orange-400 text-ink font-bold text-sm"
            >
              <Store className="w-4 h-4" /> Sell your site
            </button>
          </Tile>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map(s => (
              <Tile key={s.title} className="p-6 flex gap-4">
                <IconSquare icon={s.icon} />
                <div>
                  <h2 className="text-white font-bold">{s.title}</h2>
                  <p className="mt-1 text-sm text-zinc-400 leading-relaxed">{s.body}</p>
                </div>
              </Tile>
            ))}
          </div>

          <div className="mt-10">
            <MarketplaceBrowser
              emptyAction={(
                <button onClick={sell} className="h-10 px-5 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold">
                  Sell your site
                </button>
              )}
            />
          </div>
        </div>
      </section>

      <SellSiteDialog open={selling} onClose={() => setSelling(false)} />
      <AuthModal isOpen={authOpen} onClose={setAuthOpen} description="Sign in to sell your site on the BuildersLab Marketplace." />
    </div>
  );
}
